import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, Building2Icon, CheckCircle2Icon, FlameIcon, XIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { Button } from '../../components/ui/Button';
import { DisposalType, MedicineBatch, StockDisposal } from '../../types';
type DisposeStockModalProps = {
  batch: MedicineBatch;
  medicineName: string;
  onClose: () => void;
};
type Step = 'form' | 'review' | 'done';
const REASONS = ['Expired', 'Damaged', 'Recalled', 'Near expiry', 'Other'];
const disposalOptions: Array<{
  value: DisposalType;
  title: string;
  description: string;
  icon: typeof FlameIcon;
  accent: string;
}> = [{
  value: 'Donated To',
  title: 'Donated To',
  description: 'Items are handed over to another facility.',
  icon: Building2Icon,
  accent: 'text-blue-600'
}, {
  value: 'Incineration',
  title: 'Incineration',
  description: 'Items are destroyed by incineration.',
  icon: FlameIcon,
  accent: 'text-red-600'
}];
const newRequestId = () => typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
export function DisposeStockModal({
  batch,
  medicineName,
  onClose
}: DisposeStockModalProps) {
  const {
    currentUser,
    disposeStock
  } = useData();
  // One id per opened form: a retry or double-submit reuses it and the
  // backend applies the deduction at most once.
  const [requestId] = useState(newRequestId);
  const [step, setStep] = useState<Step>('form');
  const [quantity, setQuantity] = useState('');
  const [disposalType, setDisposalType] = useState<DisposalType | ''>('');
  const [facility, setFacility] = useState('');
  const [reason, setReason] = useState('Expired');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<StockDisposal | null>(null);
  const submittingRef = useRef(false);
  const available = batch.quantityRemaining;
  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingRef.current) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const validate = () => {
    const next: Record<string, string> = {};
    const qty = Number(quantity);
    if (!quantity.trim()) next.quantity = 'Enter the quantity to dispose.';else if (!Number.isInteger(qty) || qty <= 0) next.quantity = 'Quantity must be a whole number greater than zero.';else if (qty > available) next.quantity = `Only ${available} unit${available === 1 ? '' : 's'} available in this batch.`;
    if (!disposalType) next.disposalType = 'Select the type of disposal.';
    if (disposalType === 'Donated To' && !facility.trim()) next.facility = 'Enter the name of the receiving facility.';
    if (!reason) next.reason = 'Select a reason for disposal.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleReview = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (validate()) setStep('review');
  };
  const handleConfirm = async () => {
    if (submittingRef.current || !disposalType) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');
    const response = await disposeStock({
      requestId,
      batchId: batch.id,
      quantity: Number(quantity),
      disposalType,
      receivingFacility: disposalType === 'Donated To' ? facility.trim() : undefined,
      reason,
      notes: notes.trim() || undefined
    });
    submittingRef.current = false;
    setIsSubmitting(false);
    if (response.ok && response.disposal) {
      setResult(response.disposal);
      setStep('done');
    } else {
      setSubmitError(`${response.error || 'The disposal could not be saved.'} Inventory was not changed.`);
    }
  };
  const inputClass = 'block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="dialog" aria-modal="true" aria-labelledby="dispose-stock-title" data-id="element-8698" data-mp="1x6aebb7e9bv6">
      <div className="flex max-h-[95vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl" data-id="element-8699" data-mp="1n71l4kkcs19v">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4" data-id="element-8700" data-mp="1j6rdz4ktr4tf">
          <div data-id="element-8701" data-mp="brd03yqntcbp">
            <h2 id="dispose-stock-title" className="text-lg font-bold text-slate-900" data-id="element-8702" data-mp="7rvr4vc0xx1s">
              {step === 'done' ? 'Disposal completed' : 'Dispose stock'}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-8703" data-mp="2jd4o2qle4vog">
              {medicineName} · Batch {batch.batchNumber}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={isSubmitting} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50" aria-label="Close" data-id="element-8704" data-mp="2sfdxte8u4l0s">
            <XIcon className="h-5 w-5" data-id="element-8705" data-mp="374aeu0svd8dj" />
          </button>
        </header>

        {step === 'form' && <form onSubmit={handleReview} noValidate className="flex min-h-0 flex-1 flex-col" data-id="element-8706" data-mp="3b65ez1z2dfpq">
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5" data-id="element-8707" data-mp="3g605h4exg3a9">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg bg-slate-50 p-4 text-sm sm:grid-cols-3" data-id="element-8708" data-mp="1gsdx5f6kzpj7">
                <div className="col-span-2 sm:col-span-3" data-id="element-8709" data-mp="1lr79v5e1ncib">
                  <dt className="text-xs text-slate-500" data-id="element-8710" data-mp="1lrpdwo6y6v0s">Item / Drug name</dt>
                  <dd className="font-semibold text-slate-900" data-id="element-8711" data-mp="2p3619nfqcr2n">{medicineName}</dd>
                </div>
                <div data-id="element-8712" data-mp="2k4uqnahm2cjd">
                  <dt className="text-xs text-slate-500" data-id="element-8713" data-mp="2k5cxf11wg95j">Batch number</dt>
                  <dd className="font-medium text-slate-900" data-id="element-8714" data-mp="2y4mbnv6jot6v">{batch.batchNumber}</dd>
                </div>
                <div data-id="element-8715" data-mp="2t2evsuilgsz9">
                  <dt className="text-xs text-slate-500" data-id="element-8716" data-mp="2t6b1fjcc8go4">Available stock</dt>
                  <dd className="font-semibold tabular-nums text-slate-900" data-id="element-8717" data-mp="37znwwfxp9b46">
                    {available} units
                  </dd>
                </div>
                <div data-id="element-8718" data-mp="3gu2g2tlcp5sk">
                  <dt className="text-xs text-slate-500" data-id="element-8719" data-mp="3gs95x4bj43ft">Date of disposal</dt>
                  <dd className="font-medium text-slate-900" data-id="element-8720" data-mp="3crp7lxf7yts3">{today}</dd>
                </div>
                <div className="col-span-2 sm:col-span-3" data-id="element-8721" data-mp="3qr72muj3z39w">
                  <dt className="text-xs text-slate-500" data-id="element-8722" data-mp="3qpm46ttob06r">Disposed by</dt>
                  <dd className="font-medium text-slate-900" data-id="element-8723" data-mp="3lu6v0f6m1sgo">
                    {currentUser?.fullName || '—'}
                  </dd>
                </div>
              </dl>

              <div data-id="element-8724" data-mp="e8e0th8w64xl">
                <label htmlFor="dispose-quantity" className="block text-sm font-medium text-slate-800" data-id="element-8725" data-mp="e7m6e9r16d8w">
                  Quantity to dispose <span className="text-red-600" aria-hidden="true" data-id="element-8726" data-mp="3vic5g7qz2dqh">*</span>
                </label>
                <input id="dispose-quantity" type="number" inputMode="numeric" min={1} max={available} step={1} value={quantity} onChange={e => setQuantity(e.target.value)} aria-invalid={Boolean(errors.quantity)} aria-describedby={errors.quantity ? 'dispose-quantity-error' : undefined} className={`mt-1 ${inputClass} ${errors.quantity ? 'border-red-400' : ''}`} data-id="element-8727" data-mp="4c1a8s8n8772" />
                {errors.quantity && <p id="dispose-quantity-error" className="mt-1 text-sm text-red-700" data-id="element-8728" data-mp="3o668hzhf6at6">
                    {errors.quantity}
                  </p>}
              </div>

              <fieldset aria-describedby={errors.disposalType ? 'dispose-type-error' : undefined} data-id="element-8729" data-mp="3j58qbmwetgro">
                <legend className="text-sm font-medium text-slate-800" data-id="element-8730" data-mp="3e9tef0hypuxw">
                  Type of Disposal <span className="text-red-600" aria-hidden="true" data-id="element-8731" data-mp="39azy70bajpu1">*</span>
                </legend>
                <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2" data-id="element-8732" data-mp="359en9kibhlmh">
                  {disposalOptions.map(({
                value,
                title,
                description,
                icon: Icon,
                accent
              }) => {
                const checked = disposalType === value;
                return <label key={value} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${checked ? 'border-teal-600 ring-1 ring-teal-600' : 'border-slate-300 hover:bg-slate-50'}`} data-id="element-8733" data-mp="2qjqmcke1soza">
                        <input type="radio" name="disposalType" value={value} checked={checked} onChange={() => setDisposalType(value)} className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500" data-id="element-8734" data-mp="x0gjq75pzdhc" />
                        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${accent}`} aria-hidden="true" data-id="element-8735" data-mp="dbzfu8jsi888" />
                        <span data-id="element-8736" data-mp="iaj44fluvw3r">
                          <span className="block text-sm font-semibold text-slate-900" data-id="element-8737" data-mp="ibu74d7h8ztt">{title}</span>
                          <span className="block text-xs text-slate-500" data-id="element-8738" data-mp="n8ub8ikrnsia">{description}</span>
                        </span>
                      </label>;
              })}
                </div>
                {errors.disposalType && <p id="dispose-type-error" className="mt-1 text-sm text-red-700" data-id="element-8739" data-mp="3vpp6bbvbo8lt">
                    {errors.disposalType}
                  </p>}
              </fieldset>

              {disposalType === 'Donated To' && <div data-id="element-8740" data-mp="247kbptws5jww">
                  <label htmlFor="dispose-facility" className="block text-sm font-medium text-slate-800" data-id="element-8741" data-mp="246r5qv5nayft">
                    Name of Receiving Facility <span className="text-red-600" aria-hidden="true" data-id="element-8742" data-mp="2e6ruwxq748rx">*</span>
                  </label>
                  <input id="dispose-facility" type="text" value={facility} onChange={e => setFacility(e.target.value)} placeholder="Enter the name of the facility" aria-invalid={Boolean(errors.facility)} aria-describedby={errors.facility ? 'dispose-facility-error' : undefined} className={`mt-1 ${inputClass} ${errors.facility ? 'border-red-400' : ''}`} data-id="element-8743" data-mp="1gj9jtg92aukr" />
                  {errors.facility && <p id="dispose-facility-error" className="mt-1 text-sm text-red-700" data-id="element-8744" data-mp="12p9v29bqjtao">
                      {errors.facility}
                    </p>}
                </div>}

              <div data-id="element-8745" data-mp="3717rnqpd3yo2">
                <label htmlFor="dispose-reason" className="block text-sm font-medium text-slate-800" data-id="element-8746" data-mp="370oiozhowkm6">
                  Reason for disposal <span className="text-red-600" aria-hidden="true" data-id="element-8747" data-mp="3211yg63g243v">*</span>
                </label>
                <select id="dispose-reason" value={reason} onChange={e => setReason(e.target.value)} className={`mt-1 ${inputClass}`} data-id="element-8748" data-mp="2x41xg9lgbrjm">
                  {REASONS.map(item => <option key={item} value={item} data-id="element-8749" data-mp="2jc5bknadmdvo">
                      {item}
                    </option>)}
                </select>
              </div>

              <div data-id="element-8750" data-mp="2eedc987ejphg">
                <label htmlFor="dispose-notes" className="block text-sm font-medium text-slate-800" data-id="element-8751" data-mp="29cxnb523syts">
                  Notes <span className="font-normal text-slate-500" data-id="element-8752" data-mp="1zpafqrywtq0v">(optional)</span>
                </label>
                <textarea id="dispose-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} className={`mt-1 ${inputClass}`} data-id="element-8753" data-mp="24p6mv02wlxkn" />
              </div>
            </div>
            <footer className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3" data-id="element-8754" data-mp="2iftanx74m31y">
              <Button type="button" variant="outline" onClick={onClose} data-id="element-8755" data-mp="2nf4tdbr1zim6">
                Cancel
              </Button>
              <Button type="submit" variant="danger" data-id="element-8756" data-mp="29oszp8etlbsx">
                Review disposal
              </Button>
            </footer>
          </form>}

        {step === 'review' && <>
            <div className="space-y-4 px-5 py-5" data-id="element-8757" data-mp="31e4j6mqay3jx">
              <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-id="element-8758" data-mp="31dcmf8kyxzs2">
                <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-8759" data-mp="uqf2ty13vtsd" />
                Confirming removes these units from inventory permanently. The
                record cannot be edited afterwards.
              </p>
              <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm" data-id="element-8760" data-mp="14g6tih6342rk">
                {[['Item', medicineName], ['Batch', batch.batchNumber], ['Quantity to dispose', `${quantity} units`], ['Batch balance after', `${available - Number(quantity)} units`], ['Disposal method', disposalType], ...(disposalType === 'Donated To' ? [['Receiving facility', facility.trim()]] : []), ['Reason', reason], ['Disposed by', currentUser?.fullName || '—']].map(([label, value]) => <div key={label} className="flex justify-between gap-4 px-4 py-2" data-id="element-8761" data-mp="1s61936hsg5fi">
                    <dt className="text-slate-500" data-id="element-8762" data-mp="1qrxrrn7auo6">{label}</dt>
                    <dd className="text-right font-semibold text-slate-900" data-id="element-8763" data-mp="6ns5s83m27hx">{value}</dd>
                  </div>)}
              </dl>
              {submitError && <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-id="element-8764" data-mp="foyp0oxn9wev">
                  {submitError}
                </p>}
            </div>
            <footer className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3" data-id="element-8765" data-mp="pmwbb6vy1j4u">
              <Button type="button" variant="outline" onClick={() => setStep('form')} disabled={isSubmitting} data-id="element-8766" data-mp="udus07kugj8m">
                Back
              </Button>
              <Button type="button" variant="danger" onClick={handleConfirm} disabled={isSubmitting} isLoading={isSubmitting} data-id="element-8767" data-mp="13fc99t9zmqyw">
                {isSubmitting ? 'Saving…' : submitError ? 'Retry disposal' : 'Confirm Disposal'}
              </Button>
            </footer>
          </>}

        {step === 'done' && result && <>
            <div className="space-y-4 px-5 py-6 text-center" role="status" data-id="element-8768" data-mp="14j4g66cxkxwo">
              <CheckCircle2Icon className="mx-auto h-10 w-10 text-emerald-600" aria-hidden="true" data-id="element-8769" data-mp="218bvgwmyias5" />
              <p className="text-base font-semibold text-slate-900" data-id="element-8770" data-mp="1w8ph2jve6ohr">
                {result.quantity} units of {result.medicineName} disposed
                {result.disposalType === 'Donated To' ? ` — donated to ${result.receivingFacility}` : ' by incineration'}
                .
              </p>
              <p className="text-sm text-slate-600" data-id="element-8771" data-mp="2jzuwwz1c1gs2">
                Inventory updated: {result.stockBefore} → {' '}
                <span className="font-semibold text-slate-900" data-id="element-8772" data-mp="2f60f615afbvm">{result.stockAfter}</span>{' '}
                units on hand. The record is saved in disposal history.
              </p>
            </div>
            <footer className="flex justify-end border-t border-slate-200 px-5 py-3" data-id="element-8773" data-mp="ib8hbil32r2o">
              <Button type="button" onClick={onClose} data-id="element-8774" data-mp="3if463taan16">
                Done
              </Button>
            </footer>
          </>}
      </div>
    </div>;
}